import find from 'lodash/find'
import get from 'lodash/get'
import isNull from 'lodash/isNull'
import queryString from 'query-string'
import { matchPath, PathMatch } from 'react-router-dom'

import { getLocation } from 'routing/RoutingService'

import { PageId } from './pages'

interface PagePathDefinition {
    page: PageId
    path: string
}

// Order matters here, it's not easy to reorder this list
export const PAGES_AND_PATHS: PagePathDefinition[] = [
    { page: 'adAccountsSummaryPage', path: '/advertising_accounts' },
    { page: 'adAccountPage', path: '/advertising_accounts/:adAccountId' },
    { page: 'amazonCampaignCreationPage', path: '/amazon/campaigns/create' },
    {
        page: 'brandCampaignPage',
        path: '/advertising_accounts/:brandId/campaigns/:campaignId',
    },
    {
        page: 'brandCampaignKeywordPage',
        path: '/advertising_accounts/:brandId/campaigns/:campaignId/keywords/:keywordId',
    },
    {
        page: 'brandCampaignProductAdPage',
        path: '/advertising_accounts/:brandId/campaigns/:campaignId/productAds/:productAdId',
    },
    { page: 'bulkUpdatesPage', path: '/settings/bulk-updates' },
    { page: 'campaignsSummaryPage', path: '/campaigns' },
    { page: 'campaignPage', path: '/campaigns/:campaignId' },
    {
        page: 'campaignKeywordPage',
        path: '/campaigns/:campaignId/keywords/:keywordId',
    },
    {
        page: 'campaignProductAdPage',
        path: '/campaigns/:campaignId/productAds/:productAdId',
    },
    {
        page: 'categoryPerformanceSummaryPage',
        path: '/category-performance',
    },
    {
        page: 'categoryDetailsPage',
        path: '/category-performance/:categoryId',
    },
    { page: 'adGroupPage', path: '/campaigns/:campaignId/adGroups/:adGroupId' },
    {
        page: 'adGroupKeywordPage',
        path: '/campaigns/:campaignId/adGroups/:adGroupId/keywords/:keywordId',
    },
    { page: 'dashboardsSummaryPage', path: '/dashboards' },
    { page: 'dashboardPrintPage', path: '/dashboards/:dashboardId/print' },
    { page: 'homePagePrintPage', path: '/home/print' },
    { page: 'dashboardPage', path: '/dashboards/:dashboardId' },
    { page: 'debugColumnsPage', path: '/column-debugger' },
    { page: 'debugIconsPage', path: '/icon-debugger' },
    { page: 'debugMetricsPage', path: '/metric-debugger' },
    { page: 'debugSandboxPage', path: '/sandbox' },
    { page: 'dspSummaryPage', path: '/dsp' },
    { page: 'dspAdvertiserPage', path: '/dsp/advertisers/:advertiserId' },
    { page: 'dspOrderPage', path: '/dsp/orders/:orderId' },
    { page: 'dspLineItemPage', path: '/dsp/lineItems/:lineItemId' },
    { page: 'productsSummaryPage', path: '/products' },
    { page: 'productPage', path: '/products/:asin/:countryCode' },
    {
        page: 'productProductAdPage',
        path: '/products/:asin/:countryCode/productAds/:productAdId',
    },
    { page: 'productCatalogPage', path: '/product-catalog' },
    { page: 'productDetailPage', path: '/product-catalog/:asin/:countryCode' },
    { page: 'targetingSummaryPage', path: '/targeting' },
    { page: 'adGroupsSummaryPage', path: '/ad-groups' },
    { page: 'keywordPage', path: '/targeting/keywords/:keywordId' },
    { page: 'keywordResearchPage', path: '/keyword-research' },
    { page: 'keywordResearchListsPage', path: '/keyword-research-lists' },
    {
        page: 'keywordResearchListDetailPage',
        path: '/keyword-research-lists/:keywordResearchListId',
    },
    { page: 'keywordResearchV2Page', path: '/keyword-research-v2' },
    { page: 'labelPage', path: '/settings/labels/:labelId' },
    { page: 'metricsGlossaryPage', path: '/metric-glossary' },
    { page: 'segmentsSummaryPage', path: '/segments' },
    { page: 'segmentCreationPage', path: '/segments/create' },
    { page: 'segmentEditPage', path: '/segments/:segmentId/edit' },
    { page: 'segmentPage', path: '/segments/:segmentId' },
    { page: 'marketAnalysis', path: '/market-analysis' },
    { page: 'sovKeywordsSummaryPage', path: '/sov' },
    { page: 'sovKeywordPage', path: '/sov/:sovKeywordId' },
    {
        page: 'sovKeywordSearchResultPage',
        path: '/sov/:sovKeywordId/search/:scheduledDate',
    },
    { page: 'authPage', path: '/auth' },
    { page: 'authSignUpPage', path: '/auth/signup' },
    { page: 'authSignInMfaPage', path: '/auth/mfa' },
    { page: 'authSignUpMfaSetupPage', path: '/user/mfa/setup' },
    { page: 'authInvitationSignupPage', path: '/auth/signup/invitation' },
    { page: 'authVerifyEmailPage', path: '/auth/signup/verify' },
    { page: 'authOrganizationPage', path: '/auth/signup/organization' },
    { page: 'authForgotPasswordPage', path: '/auth/password/forgot' },
    { page: 'authResetPasswordPage', path: '/auth/password/reset' },
    { page: 'settingsPage', path: '/settings' },
    {
        page: 'organizationGroupPage',
        path: '/settings/groups/:organizationGroupId',
    },
    { page: 'aiPage', path: '/ai' },
    { page: 'aiReportPrintPage', path: '/ai-reports/:reportId/print' },
    { page: 'aiReportSharedPage', path: '/s' },
    { page: 'automationPage', path: '/automation' },
    { page: 'automationProfilePage', path: '/automation/profiles/:profileId' },
    { page: 'reportsSummaryPage', path: '/reports' },
    { page: 'portfoliosSummaryPage', path: '/portfolios' },
    { page: 'portfolioPage', path: '/portfolios/:portfolioId' },
    { page: 'insightsPage', path: '/insights' },
    { page: 'alertPage', path: '/settings/alerts/:alertId' },
    { page: 'alertCreationPage', path: '/settings/alerts/create' },
    { page: 'rulebookPage', path: '/rulebooks/:rulebookId' },
    { page: 'zendeskSSOPage', path: '/zendesk/sso' },
    { page: 'distributionAnalysisPage', path: '/distribution-analysis' },
    // Home Page should always be last in this list so that getPage
    // will work for highlighting nav items when in a nested route
    { page: 'homePage', path: '/' },
]

const PAGE_TO_REDUCER_KEY_MAP = {
    ['adGroupKeywordPage']: 'keywordPage',
    ['brandCampaignPage']: 'campaignPage',
    ['brandCampaignKeywordPage']: 'keywordPage',
    ['brandCampaignProductAdPage']: 'productAdPage',
    ['campaignKeywordPage']: 'keywordPage',
    ['campaignProductAdPage']: 'productAdPage',
    ['productProductAdPage']: 'productAdPage',
} as const

export const getPath = (page: PageId): string =>
    get(find(PAGES_AND_PATHS, { page }), 'path', '')

export const tryGetPath = (page: string): string => getPath(page as PageId)

/**
 * A page's path as the browser shows it: under the router's basename, which is
 * `/` in production but `/<build>/` on PR builds. For building a full URL, or
 * matching one, outside the router.
 */
export const getBrowserPath = (page: PageId): string =>
    `${ASSET_PATH.replace(/\/$/, '')}${getPath(page)}`

interface Page {
    page?: PageId
    match?: PathMatch | null
}

export const getPage = (path?: string, exact = true): Page => {
    if (!path) {
        return {}
    }

    let match = null
    const pageAndPath = find(PAGES_AND_PATHS, (item) => {
        match = matchPath({ path: item.path, end: exact }, path)
        return !isNull(match)
    })
    if (pageAndPath) {
        const { page } = pageAndPath
        return { page: get(PAGE_TO_REDUCER_KEY_MAP, [page], page), match }
    }
    return {}
}

export const getCurrentPage = (exact = true): Page => {
    const location = getLocation()
    return getPage(location?.pathname, exact)
}

export const getCurrentTab = (): string | undefined => {
    const location = getLocation()
    const query: { tab?: string } = queryString.parse(location?.search ?? '')
    return query.tab
}
